
// withCandidate.js — PDF + Select Theme + Sticky centré verticalement
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { CircularProgress } from "@material-ui/core";
import moment from "moment";
import "moment/locale/fr";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import localStorageService from "../../../../services/localStorageService";
import { getCandidate } from "../../../../redux/actions/admin/CandidateActions";

const THEME_KEY = "cv_theme_choice";
const CUSTOM_THEME_KEY = "cv_theme_custom";


const toSafeFileName = (str) => {
  if (!str) return "resume";
  return str
    .normalize("NFD")                   // décompose les accents
    .replace(/[\u0300-\u036f]/g, "")    // supprime les diacritiques
    .replace(/[\/\\?%*:|"<>]/g, "-")    // caractères interdits dans les noms de fichier
    .replace(/\s+/g, " ")               // normalise les espaces
    .trim();
};

function withCandidate(TemplateBody, fileName) {
  const Wrapped = (props) => {
    const { storeCandidate, getCandidate } = props;
    const [candidateId, setCandidateId] = useState("");
    const [candidate, setCandidate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);

    const [theme, setTheme] = useState("auto");

    const pageRef = useRef(null);
    const mqlRef = useRef(null);

    /* ----------------------------------------------
       INIT
    ---------------------------------------------- */
    useEffect(() => {
      moment.locale("fr");
      const c = localStorageService.getItem("malibaara_auth_candidate");
      if (c?.id) setCandidateId(c.id);
      window.scrollTo(0, 0);
      
      // 🔄 Vider le thème à chaque refresh
      try {
        localStorage.removeItem(THEME_KEY);       // "cv_theme_choice"
        localStorage.removeItem(CUSTOM_THEME_KEY); // "cv_theme_custom"
      } catch {}
      setTheme("auto"); // on repart en auto (système)
    }, []);

    // Déstructure ce dont on a besoin du store
    const storeCand = storeCandidate?.candidate;
    const storeCandId = storeCand?.id;

    /** Effet 1 : fetch si nécessaire (pas la peine si le store a déjà le bon candidat) */
    useEffect(() => {
      if (!candidateId) return;
      setIsLoading(true);

      const needFetch = !storeCandId || storeCandId !== candidateId;

      if (needFetch) {
        getCandidate(candidateId)
          .then((resp) => {
            setCandidate(resp);
          })
          .catch((err) => {
            console.error("Erreur lors du chargement du candidat :", err);
            alert("Impossible de charger le CV pour le moment. Veuillez réessayer plus tard.");
            setCandidate({});
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        // Le store a déjà le bon candidat : pas d'appel API
        setIsLoading(false);
      }
    }, [candidateId, storeCandId, getCandidate]);

    /** Effet 2 : synchronise l'état local quand le store fournit le bon candidat */
    useEffect(() => {
      if (storeCand && storeCand.id === candidateId) {
        setCandidate(storeCand);
      }
    }, [storeCand, candidateId]);


    /* ----------------------------------------------
       THEMES: load + system listener
    ---------------------------------------------- */
    useEffect(() => {
      const saved = localStorage.getItem(THEME_KEY) || "auto";
      setTheme(saved);

      mqlRef.current = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => {
        if ((localStorage.getItem(THEME_KEY) || "auto") === "auto") {
          applyTheme("auto");
        }
      };
      mqlRef.current.addEventListener?.("change", handler);

      return () => {
        mqlRef.current?.removeEventListener?.("change", handler);
      };
    }, []);

    useEffect(() => {
      applyTheme(theme);
    }, [theme]);

    const applyTheme = (choice) => {
      const container = pageRef.current;
      if (!container) return;

      container.removeAttribute("style");

      let actual = choice;

      if (choice === "auto") {
        const dark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        actual = dark ? "noir-or" : "blue-pro";
      }

      container.setAttribute("data-theme", actual);

      if (actual === "custom") {
        try {
          const raw = localStorage.getItem(CUSTOM_THEME_KEY);
          if (!raw) return;
          const vars = JSON.parse(raw);
          Object.entries(vars).forEach(([k, v]) =>
            container.style.setProperty(`--${k}`, v)
          );
        } catch {}
      }
    };

    /* ----------------------------------------------
       API applyCvTheme()
    ---------------------------------------------- */
    useEffect(() => {
      window.applyCvTheme = (obj) => {
        try {
          localStorage.setItem(CUSTOM_THEME_KEY, JSON.stringify(obj));
          localStorage.setItem(THEME_KEY, "custom");
          setTheme("custom");
        } catch {
          alert("Invalid custom theme JSON.");
        }
      };
      return () => {
        delete window.applyCvTheme;
      };
    }, []);

    const onThemeChange = (e) => {
      const value = e.target.value;
      setTheme(value);
      localStorage.setItem(THEME_KEY, value);
    };

    /* ----------------------------------------------
       PDF
    ---------------------------------------------- */
    const onDownloadPDF = async () => {
      if (!pageRef.current) return;

      try {
        setIsGenerating(true);
        window.scrollTo(0, 0);
        await new Promise((r) => setTimeout(r, 50));

        const canvas = await html2canvas(pageRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
                
        const fullName = `${candidate?.firstName || ""} ${candidate?.lastName || ""}`.trim();
        const safeName = toSafeFileName(fullName);

        const templateName = toSafeFileName(fileName);  // ex: resume_red_sidebar

        const storedTheme = localStorage.getItem("cv_theme_choice") || "auto";
        const safeTheme = toSafeFileName(storedTheme);

        const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");

        const finalName = `${safeName} - ${templateName} - ${safeTheme} - ${timestamp}.pdf`;

        const img = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const w = pdf.internal.pageSize.getWidth();
        const h = pdf.internal.pageSize.getHeight();

        pdf.addImage(img, "PNG", 0, 0, w, h);
        pdf.save(finalName);
      } catch (e) {
        console.error(e);
        alert("Erreur lors de la génération du PDF.");
      } finally {
        setIsGenerating(false);
      }
    };

    /* ----------------------------------------------
       LOADING
    ---------------------------------------------- */
    if (isLoading) {
      return (
        <div
          style={{
            display: "grid",
            placeItems: "center",
            minHeight: "50vh",
            gap: 12,
            color: "#fff",
          }}
        >
          <CircularProgress />
          <div>Chargement du CV...</div>
        </div>
      );
    }

    /* ----------------------------------------------
       RENDER
    ---------------------------------------------- */
    return (
      <>
        <style>{`
          /* -------- GLOBAL LAYOUT -------- */
          .cv-preview {
            display: flex;
          }

          /* -------- OUTILS (sticky centered) -------- */
          .cv-tools {
            position: fixed;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            width: 280px;
            z-index: 2000;

            display: flex;
            flex-direction: column;
            gap: 14px;
          }

          .cv-tools .card {
            background: #111827;
            color: #e5e7eb;
            border-radius: 10px;
            padding: 12px 14px;
            box-shadow: 0 2px 8px rgba(0,0,0,.25);
          }

          .cv-tools select {
            width: 100%;
            height: 38px;
            border-radius: 8px;
            border: 1px solid #374151;
            background: #1f2937;
            color: #d1d5db;
            padding: 0 10px;
            font-size: 14px;
          }

          .cv-tools .btn {
            width: 100%;
            background: #2563eb;
            border: none;
            color: #fff;
            padding: 12px 14px;
            border-radius: 8px;
            font-size: 15px;
            cursor: pointer;
            box-shadow: 0 2px 6px rgba(0,0,0,.25);
          }

          .cv-tools .btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }

          /* Décalage du CV */
          .template-page {
            margin-left: calc(280px + 48px);
          }

          /* -------- Responsive mobile -------- */
          @media (max-width: 1280px) {
            .cv-tools {
              position: static;
              transform: none;
              width: 100%;
              margin-bottom: 20px;
            }

            .template-page {
              margin-left: auto;
              margin-right: auto;
            }

            .cv-preview {
              flex-direction: column;
              align-items: center;
            }
          }

          @media print {
            .cv-tools { display: none !important; }
            .template-page { margin-left: 0 !important; }
          }
        `}</style>

        <div className="cv-preview">
          {/* OUTILS */}
          <div className="cv-tools">
            <div className="card">
              <label style={{ fontSize: 12, opacity: 0.8, display: "block", marginBottom: 6 }}>
                Thème
              </label>

              <select value={theme} onChange={onThemeChange}>
                <option value="auto">Auto (système)</option>
                <option value="blue-pro">Bleu Pro</option>
                <option value="slate">Slate Pro</option>
                <option value="emerald">Vert Émeraude</option>
                <option value="royal">Violet Royal</option>
                <option value="bordeaux">Rouge Bordeaux</option>
                <option value="noir-or">Noir & Or</option>
                <option value="bleu-clair">Bleu Clair</option>
                <option value="orange">Orange Moderne</option>
                <option value="teal">Océan</option>
                <option value="forest">Forest</option>
                <option value="rose">Rose Foncé</option>
                <option value="indigo">Indigo</option>
                <option value="malibaara">Malibaara</option>
                <option value="custom">Custom (console)</option>
              </select>
            </div>

            <button
              className="btn"
              disabled={isGenerating}
              onClick={onDownloadPDF}
            >
              {isGenerating ? "Génération..." : "Télécharger PDF"}
            </button>
          </div>

          {/* CV */}
          <div className="template-page" ref={pageRef}>
            <TemplateBody candidate={candidate} />
          </div>
        </div>
      </>
    );
  };

  Wrapped.propTypes = {
    storeCandidate: PropTypes.object.isRequired,
    getCandidate: PropTypes.func.isRequired,
  };

  return connect(
    (state) => ({ storeCandidate: state.candidate }),
    { getCandidate }
  )(Wrapped);
}

export default withCandidate;
